/* eslint-disable no-param-reassign */
import {
  Person,
  Phone,
  AllInbox,
  Email,
  Event,
  Http,
  QuestionMark,
  AltRoute,
} from '@mui/icons-material';
import { formatRelative, isValid } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useState, useEffect, useMemo, useCallback } from 'react';
import useMiViewTargetCardConfig from './MiViewTargetCard.config';
import { Channel, Tactic, VeevaVaultDocument } from 'generated/maya.types';

const useMiViewTargetCard = (
  tactics: Tactic[],
  AEDocuments: VeevaVaultDocument[],
  IVADocuments: VeevaVaultDocument[],
) => {
  const [openTooltip, setOpenTooltip] = useState<boolean[]>([]);

  useEffect(() => {
    if (tactics?.length) {
      setOpenTooltip(Array(tactics.length).fill(false));
    }
  }, [tactics.length]);

  const extractLastNumber = (formattedId: string): number => {
    if (!formattedId) return 0;
    const parts = formattedId.split('-');
    const lastPart = parts[parts.length - 1];
    const num = parseInt(lastPart, 10);
    // eslint-disable-next-line no-restricted-globals
    return isNaN(num) ? 0 : num;
  };

  const getChildTactics = (tactic: Tactic) => {
    const childTactics = tactics.filter((tacticItem) => tacticItem?.parentTactic?._id === tactic._id)
      ?.sort((a: Tactic, b: Tactic) => {
        return extractLastNumber(a.formattedTacticId) - extractLastNumber(b.formattedTacticId);
      });

    return childTactics;
  };

  const getActionOrder = (tactic: Tactic) => {
    if (tactic.channel !== Channel.group) {
      return tactic.actionOrder;
    }

    const childTactics = getChildTactics(tactic);
    return childTactics?.length ? childTactics[0].actionOrder : 0;
  };

  const formattedTactics = useMemo(() => {
    if (!tactics?.length) return [];

    return tactics.filter((tactic) => !tactic.parentTactic).map((tacticItem) => {
      const tactic = { ...tacticItem };

      if (tactic.channel === Channel.group) {
        tactic.title = 'Multi';
        tactic.actionOrder = getActionOrder(tactic);
      }

      return tactic;
    })
      .sort((a, b) => Number(a.actionOrder) - Number(b.actionOrder));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tactics]);

  const handleTooltipOpening = useCallback((index: number) => {
    setOpenTooltip((prev) => prev.map((item, i) => (i === index ? !item : false)));
  }, []);

  const handleTooltipClosing = useCallback((index: number) => {
    setOpenTooltip((prev) => prev.map((item, i) => (i === index ? false : item)));
  }, []);

  const getSubtitle = useCallback(
    (tactic: Tactic): string => {
      if (!tactic) return '';

      let tacticContent: string | undefined | null = '';

      if (tactic?.keyMessage?.name) {
        tacticContent = tactic.keyMessage.name;
      } else if (tactic.approvedDocumentId) {
        const document = AEDocuments?.find((doc) => doc.id === Number(tactic.approvedDocumentId));
        tacticContent = document?.name;
      } else if (tactic.dropMaterial?.name) {
        tacticContent = tactic.dropMaterial.name;
      } else if (tactic.IVADocumentId) {
        const document = IVADocuments?.find((doc) => doc.id === Number(tactic.IVADocumentId));
        tacticContent = document?.name;
      } else {
        tacticContent = tactic.assetName;
      }

      return tacticContent || '';
    },
    [AEDocuments, IVADocuments],
  );

  const getTooltipContent = useCallback(
    (tactic: Tactic): string[] => {
      if (tactic.channel !== Channel.group) {
        const content = [tactic.title];
        const subtitle = getSubtitle(tactic);

        if (subtitle) {
          content.push(subtitle);
        }

        return content;
      }

      const children = tactics.filter((tacticItem) => tacticItem.parentTactic?._id === tactic._id) || [];
      return children.map((child, index) => `${index + 1}) ${child.title}`);
    },
    [tactics, getSubtitle],
  );

  const getIconByChannel = useCallback((channel: Channel) => {
    switch (channel) {
      case Channel.oneToOneDropMaterial:
      case Channel.oneToOneIVA:
      case Channel.oneToOneKeyMessage:
      case Channel.oneToOne:
      case Channel.f2fMeeting:
      case Channel.f2fEvent:
        return Person;
      case Channel.telephone:
        return Phone;
      case Channel.broadcastEmail:
        return AllInbox;
      case Channel.virtualEvent:
      case Channel.hybridEvent:
        return Event;
      case Channel.approvedEmail:
      case Channel.virtualMeeting:
        return Email;
      case Channel.webLogIn:
      case Channel.webNoLogIn:
        return Http;
      case Channel.group:
        return AltRoute;
      default:
        return QuestionMark;
    }
  }, []);

  const formatDateToMMDDYYYY = (dateString: string): string => {
    if (!dateString || !isValid(new Date(dateString))) {
      return 'Invalid Date';
    }

    const utcDate = utcToZonedTime(new Date(dateString), 'UTC');
    const nowUtc = utcToZonedTime(new Date(), 'UTC');

    return formatRelative(utcDate, nowUtc);
  };

  const { targetCardConfig } = useMiViewTargetCardConfig();

  return {
    formattedTactics,
    openTooltip,
    handleTooltipOpening,
    handleTooltipClosing,
    getIconByChannel,
    formatDateToMMDDYYYY,
    targetCardConfig,
    getSubtitle,
    getTooltipContent,
  };
};

export default useMiViewTargetCard;
